import { useState } from "react";
import { Check, X, Copy, Star } from "@phosphor-icons/react";
import { toast } from "sonner";
import { AIS_SEVERITY_LABEL } from "@/lib/iss";
import CorrectionNote from "./CorrectionNote";

const confidenceStyles = {
  High: "text-[#4A5D4E] bg-[#E8EFEA] border-[#4A5D4E]/25",
  Medium: "text-[#8C3A27] bg-[#F9EDE6] border-[#8C3A27]/25",
  Low: "text-[#5C665A] bg-[#EBE7DF] border-[#5C665A]/25",
};

const aisTone = (sev) => {
  if (!sev) return "";
  if (sev <= 2) return "bg-[#E8EFEA] text-[#4A5D4E] border-[#4A5D4E]/25";
  if (sev <= 3) return "bg-[#F9EDE6] text-[#8C3A27] border-[#8C3A27]/30";
  return "bg-[#8C3A27] text-white border-[#8C3A27]";
};

export default function CodeCard({
  item,
  kind,
  index,
  onAccept,
  onReject,
  accepted,
  rejected,
  onHover,
  onLeave,
  isFavorite,
  onToggleFavorite,
  onToggleOverride,
}) {
  const [copied, setCopied] = useState(false);

  const meta = [];
  if (kind === "injury") {
    if (item.laterality) meta.push(item.laterality);
    if (item.encounter) meta.push(item.encounter);
    if (item.ais_region) meta.push(item.ais_region);
  } else if (kind === "procedure") {
    if (item.approach) meta.push(item.approach);
    if (item.body_system) meta.push(item.body_system);
  } else if (kind === "external") {
    if (item.category) meta.push(item.category);
  } else if (kind === "ais") {
    if (item.ais_region) meta.push(item.ais_region);
    if (item.laterality) meta.push(item.laterality);
  }

  // For the AIS 2015 column, swap the prominent code badge to the AIS dictionary code
  const primaryCode =
    kind === "ais" ? item.ais2015_code || "—" : item.code;
  const primaryDescription =
    kind === "ais"
      ? item.ais2015_description || item.description
      : item.description;
  const copyValue = kind === "ais" ? item.ais2015_code || item.code : item.code;

  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(copyValue);
      setCopied(true);
      toast.success(`Copied ${copyValue}`);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copy failed");
    }
  };

  const isRejected = rejected;
  const isAccepted = accepted;

  return (
    <div
      className={`group relative tc-card-lift flex flex-col bg-white border rounded-md p-3 gap-2 m-2 shadow-sm
        ${isAccepted ? "border-[#4A5D4E] ring-1 ring-[#4A5D4E]/30" : ""}
        ${isRejected ? "border-[#E5E0D8] opacity-40" : "border-[#E5E0D8] hover:border-[#8C3A27]"}`}
      data-testid={`code-card-${kind}-${index}`}
      onMouseEnter={() => onHover?.(item.evidence || "")}
      onMouseLeave={() => onLeave?.()}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className="font-mono text-[13px] font-semibold text-[#2C332A] bg-[#EBE7DF] px-1.5 py-0.5 rounded tracking-tight"
            data-testid={`code-value-${kind}-${index}`}
          >
            {primaryCode}
          </span>
          {item.confidence && (
            <span
              className={`text-[10px] font-medium px-1.5 py-0.5 rounded border uppercase tracking-wider ${
                confidenceStyles[item.confidence] || confidenceStyles.Low
              }`}
            >
              {item.confidence}
            </span>
          )}
          {(kind === "injury" || kind === "ais") && item.ais_severity ? (
            <span
              className={`text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded border uppercase tracking-wider ${aisTone(item.ais_severity)}`}
              title={`AIS ${item.ais_severity} – ${AIS_SEVERITY_LABEL[item.ais_severity] || ""}`}
              data-testid={`ais-badge-${kind}-${index}`}
            >
              AIS {item.ais_severity}
            </span>
          ) : null}
        </div>

        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 flex gap-1 transition-opacity bg-white/95 backdrop-blur-sm p-1 rounded-md border border-[#E5E0D8] shadow-sm">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleFavorite?.();
            }}
            className={`p-1 rounded ${
              isFavorite
                ? "text-[#D29063] bg-[#F9EDE6]"
                : "text-[#5C665A] hover:bg-[#F4F1EB]"
            }`}
            title={isFavorite ? "Remove from favorites" : "Save to favorites"}
            data-testid={`favorite-${kind}-${index}`}
          >
            <Star size={14} weight={isFavorite ? "fill" : "regular"} />
          </button>
          <button
            onClick={handleCopy}
            className="p-1 rounded hover:bg-[#F4F1EB] text-[#5C665A]"
            title="Copy code"
            data-testid={`copy-${kind}-${index}`}
          >
            <Copy size={14} weight={copied ? "fill" : "regular"} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onAccept?.();
            }}
            className={`p-1 rounded text-[#4A5D4E] hover:bg-[#E8EFEA] ${isAccepted ? "bg-[#E8EFEA]" : ""}`}
            title="Accept"
            data-testid={`accept-${kind}-${index}`}
          >
            <Check size={14} weight="bold" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onReject?.();
            }}
            className={`p-1 rounded text-[#8C3A27] hover:bg-[#F9EDE6] ${isRejected ? "bg-[#F9EDE6]" : ""}`}
            title="Reject"
            data-testid={`reject-${kind}-${index}`}
          >
            <X size={14} weight="bold" />
          </button>
        </div>
      </div>

      <div className="text-[13px] text-[#2C332A] leading-snug font-medium">
        {primaryDescription}
      </div>
      {kind === "ais" && item.code && (
        <div className="text-[11px] text-[#5C665A] font-mono">
          ICD-10-CM: <span className="text-[#2C332A] font-semibold">{item.code}</span>
        </div>
      )}

      {meta.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {meta.map((m, i) => (
            <span
              key={i}
              className="text-[10px] uppercase tracking-wider text-[#5C665A] bg-[#F4F1EB] px-1.5 py-0.5 rounded border border-[#E5E0D8]"
            >
              {m}
            </span>
          ))}
        </div>
      )}

      {item.evidence && (
        <div className="text-[11px] italic text-[#5C665A] border-l-2 border-[#8C3A27] pl-2 mt-1 bg-[#F9EDE6]/40 py-1.5 pr-2 rounded-r leading-relaxed">
          “{item.evidence}”
        </div>
      )}
      <CorrectionNote item={item} kind={kind} index={index} onToggle={onToggleOverride} />
      {item.validation_note && (
        <div
          className="text-[11px] text-[#8C3A27] flex items-start gap-1.5 bg-[#F9EDE6] border border-[#8C3A27]/25 rounded px-2 py-1.5 mt-1"
          data-testid={`validation-note-${kind}-${index}`}
        >
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" className="shrink-0 mt-0.5">
            <path d="M8 1l7 14H1L8 1z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
            <path d="M8 6v4M8 12.5v.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          </svg>
          <span className="leading-snug">{item.validation_note}</span>
        </div>
      )}
    </div>
  );
}
